import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth }           from "../context/AuthContext.js";
import { useLanguage }       from "../context/LanguageContext.js";
import { auth }              from "../firebase.js";
import { Navbar, Container, Nav, Button, NavDropdown } from "react-bootstrap";
import "./DashboardLayout.css";

export default function DashboardLayout({ children }) {
  const { user } = useAuth();
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const slug = encodeURIComponent(user?.displayName || user?.email || "");
  const [expanded, setExpanded] = useState(false);


  const handleLogout = async () => {
    setExpanded(false); // Close menu on logout
    await auth.signOut();
    navigate("/login", { replace: true });
  };

  const handleLanguageChange = (lang) => {
    console.log("Changing language to:", lang);
    setLanguage(lang);
  };

  // Close menu when clicking on a navigation link
  const closeMenu = () => {
    setExpanded(false);
  };

  return (
    <>
      <Navbar expand="xl" className="navbar-premium sticky-top" expanded={expanded} onToggle={setExpanded}>
        <Container fluid className="px-4">
          {/* Logo + Brand */}
          <Navbar.Brand as={Link} to="/" onClick={closeMenu} className="d-flex align-items-center gap-3 brand-container">
            <img
              src="/logo-itssoclean.png"
              alt="It's So Clean"
              height="80"
              className="navbar-logo"
            />
            <div className="brand-text">
              <strong>It's So Clean</strong>
            </div>
          </Navbar.Brand>

          <Navbar.Toggle aria-controls="navbar-nav" />

          <Navbar.Collapse id="navbar-nav">
            {/* Links principais */}
            <Nav className="me-auto ms-4">
              {/* Dashboard - sempre visível */}
              <Nav.Link as={Link} to="/" className="nav-link-custom" onClick={closeMenu}>
                <span className="nav-icon">📊</span>
                {language === "en" ? "Dashboard" : "Painel"}
              </Nav.Link>

              {/* Health & Safety - sempre visível */}
              <Nav.Link as={Link} to="/health-safety" className="nav-link-custom" onClick={closeMenu}>
                <span className="nav-icon">🛡️</span>
                {language === "en" ? "Health & Safety" : "Saúde e Segurança"}
              </Nav.Link>

              {/* Calendar - sempre visível */}
              <Nav.Link as={Link} to="/admin/calendar" className="nav-link-custom" onClick={closeMenu}>
                <span className="nav-icon">📅</span>
                {language === "en" ? "Calendar" : "Calendário"}
              </Nav.Link>

              {/* Personal Agenda - Admin only */}
              {user?.isAdmin && (
                <Nav.Link as={Link} to="/admin/personal-agenda" className="nav-link-custom" onClick={closeMenu}>
                  <span className="nav-icon">🗓️</span>
                  {language === "en" ? "My Agenda" : "Minha Agenda"}
                </Nav.Link>
              )}

              {/* Gestão - Admin e Manager */}
              {(user?.isAdmin || user?.isClientManager) && (
                <NavDropdown
                  title={
                    <>
                      <span className="nav-icon">👥</span>
                      {language === "en" ? "Management" : "Gestão"}
                    </>
                  }
                  id="management-dropdown"
                  className="nav-link-custom"
                >
                  <NavDropdown.Item as={Link} to="/admin/clients" onClick={closeMenu}>
                    <span className="nav-icon">👥</span> {language === "en" ? "Clients" : "Clientes"}
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/website-submissions" onClick={closeMenu}>
                    <span className="nav-icon">📬</span> {language === "en" ? "Website Messages" : "Mensagens Website"}
                  </NavDropdown.Item>
                  {user?.isAdmin && (
                    <>
                      <NavDropdown.Item as={Link} to="/admin/documents" onClick={closeMenu}>
                        <span className="nav-icon">📸</span> {language === "en" ? "Documents" : "Documentos"}
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/admin/email-marketing" onClick={closeMenu}>
                        <span className="nav-icon">📧</span> {language === "en" ? "Email Marketing" : "Email Marketing"}
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/admin/users" onClick={closeMenu}>
                        <span className="nav-icon">⚙️</span> {language === "en" ? "Users" : "Usuários"}
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/admin/tracking" onClick={closeMenu}>
                        <span className="nav-icon">🗺️</span> {language === "en" ? "Tracking" : "Rastreamento"}
                      </NavDropdown.Item>
                    </>
                  )}
                </NavDropdown>
              )}

              {/* Financeiro - Admin only */}
              {user?.isAdmin && (
                <NavDropdown
                  title={
                    <>
                      <span className="nav-icon">💰</span>
                      {language === "en" ? "Financial" : "Financeiro"}
                    </>
                  }
                  id="financial-dropdown"
                  className="nav-link-custom"
                >
                  <NavDropdown.Item as={Link} to="/admin/sync-xero" onClick={closeMenu}>
                    <span className="nav-icon">🔄</span> {language === "en" ? "Sync Xero" : "Sincronizar Xero"}
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/invoice-history" onClick={closeMenu}>
                    <span className="nav-icon">📄</span> {language === "en" ? "Invoices" : "Invoices"}
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/quotes-manager" onClick={closeMenu}>
                    <span className="nav-icon">📝</span> {language === "en" ? "Quotes" : "Orçamentos"}
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/feedback-manager" onClick={closeMenu}>
                    <span className="nav-icon">💬</span> {language === "en" ? "Feedback Manager" : "Gerenciador Feedback"}
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item as={Link} to="/admin/revenue" onClick={closeMenu}>
                    <span className="nav-icon">💰</span> {language === "en" ? "Revenue" : "Faturamento"}
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/profit-analysis" onClick={closeMenu}>
                    <span className="nav-icon">📊</span> {language === "en" ? "Profit Analysis" : "Análise de Lucro"}
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item as={Link} to="/admin/payroll" onClick={closeMenu}>
                    <span className="nav-icon">💵</span> {language === "en" ? "Payroll" : "Folha de Pagamento"}
                  </NavDropdown.Item>
                </NavDropdown>
              )}

              {/* Ferramentas - Admin e Manager */}
              {(user?.isAdmin || user?.isClientManager) && (
                <NavDropdown
                  title={
                    <>
                      <span className="nav-icon">🤖</span>
                      {language === "en" ? "Tools" : "Ferramentas"}
                    </>
                  }
                  id="tools-dropdown"
                  className="nav-link-custom"
                >
                  <NavDropdown.Item as={Link} to="/admin/ai-assistant" onClick={closeMenu}>
                    <span className="nav-icon">🤖</span> {language === "en" ? "AI Assistant" : "Assistente IA"}
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/ai-assistant-management" onClick={closeMenu}>
                    <span className="nav-icon">⚙️</span> {language === "en" ? "Manage AI Assistants" : "Gerenciar Assistentes"}
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item as={Link} to="/admin/training/conversations" onClick={closeMenu}>
                    <span className="nav-icon">💬</span> {language === "en" ? "Training Conversations" : "Conversas de Treinamento"}
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/admin/training/sessions" onClick={closeMenu}>
                    <span className="nav-icon">🎓</span> {language === "en" ? "Training Sessions" : "Sessões de Treinamento"}
                  </NavDropdown.Item>
                </NavDropdown>
              )}
            </Nav>

            {/* Language Selector + Saudação + Logout */}
            <Nav className="ms-auto align-items-center gap-2">
              {/* Language Selector */}
              <div className="language-selector d-flex gap-2 align-items-center">
                <button
                  type="button"
                  onClick={() => handleLanguageChange("pt")}
                  className={`flag-btn ${language === "pt" ? "active" : ""}`}
                  title="Português"
                >
                  <img
                    src="https://flagcdn.com/w40/br.png"
                    alt="Brasil"
                    width="28"
                    height="20"
                  />
                </button>
                <div className="flag-separator"></div>
                <button
                  type="button"
                  onClick={() => handleLanguageChange("en")}
                  className={`flag-btn ${language === "en" ? "active" : ""}`}
                  title="English"
                >
                  <img
                    src="https://flagcdn.com/w40/nz.png"
                    alt="New Zealand"
                    width="28"
                    height="20"
                  />
                </button>
              </div>

              {/* Divisor */}
              <div className="navbar-divider"></div>

              {user && (
                <>
                  <div className="user-info d-none d-md-flex align-items-center gap-2">
                    <span className="user-greeting">
                      {language === "en" ? "Hello" : "Olá"},
                    </span>
                    <strong className="user-name">
                      {user.displayName?.split(" ")[0] || user.email}
                    </strong>
                  </div>

                  <Button
                    variant="outline-light"
                    onClick={handleLogout}
                    className="logout-btn"
                    size="sm"
                  >
                    <span className="logout-icon">🚪</span>
                    {language === "en" ? "Logout" : "Sair"}
                  </Button>
                </>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container fluid className="px-2 px-md-4 py-3 py-md-4">{children}</Container>
    </>
  );
}
